import classNames from 'classnames';
import { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primaryBlue text-white hover:bg-primaryBlueShade focus:ring-primaryBlue border-transparent',
  secondary:
    'bg-secondaryBlue text-white hover:bg-secondaryBlueShade focus:ring-secondaryBlue border-transparent',
  tertiary:
    'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-gray-500',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-base',
};

export const Button = ({
  variant = 'primary',
  size = 'md',
  iconLeft,
  iconRight,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) => {
  return (
    <button
      className={classNames(
        // Base styles
        'inline-flex items-center justify-center',
        'rounded-md border font-medium',
        'transition-colors duration-150',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',
        // Variant styles
        variantClasses[variant],
        // Size styles
        sizeClasses[size],
        // Disabled styles
        {
          'cursor-not-allowed opacity-50': disabled,
        },
        // Custom className
        className,
      )}
      disabled={disabled}
      {...props}
    >
      {iconLeft && <span className="mr-2 flex items-center">{iconLeft}</span>}
      {children}
      {iconRight && <span className="ml-2 flex items-center">{iconRight}</span>}
    </button>
  );
};
